import { resolve } from "../di";
import { getLang, persistLanguageToServer } from "../i18n";
import type { IAuthService, IAuthState } from "./auth/types";

/**
 * Push the locally-chosen language up to the user's preferences on the
 * unauthenticated → authenticated transition so the mailer can render emails
 * in the user's preferred locale. Best-effort — failures are swallowed inside
 * `persistLanguageToServer`.
 *
 * **Why we don't just call `authService.onSignIn`:** `onSignIn` fires
 * synchronously on subscribe when the user is already authenticated (see
 * `auth-service.ts`). On every reload of an authenticated session we'd
 * re-push localStorage to the server, clobbering any preference the user set
 * from a different device. The local `pushedForUserId` flag gates the push
 * to the actual transition.
 *
 * Multi-device drift is a known limitation: the device's localStorage wins
 * the moment the user freshly signs in there. Documented in `adr/email-localization`.
 */
export function connectLanguagePreferenceSync(): () => void {
  const authService = resolve<IAuthService>("authService");
  const authState = resolve<IAuthState>("authState");

  // An already-authenticated mount is a session restore, not a sign-in — treat
  // it as "already pushed" so we don't fire on every page reload.
  let pushedForUserId: string | undefined = authState.currentUser.get()?.id;

  return authService.onSignIn((user) => {
    if (pushedForUserId === user.id) return;
    pushedForUserId = user.id;
    void persistLanguageToServer(getLang());
  });
}
